import type { Lead } from "@ir/types";

type JsonRecord = Record<string, unknown>;

const dateFields = [
  "dateOfBirth",
  "testPassDate",
  "dateMovedToUK",
  "purchaseDate",
  "insuranceStartDate",
  "currentPolicyExpiryDate",
  "overCurrentPolicyExpiryDate",
  "underCurrentPolicyExpiryDate",
] as const;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function cloneJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(cloneJsonValue);
  }

  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, cloneJsonValue(nestedValue)]),
    );
  }

  return value;
}

function removeBlankDate(target: JsonRecord, key: string): void {
  const value = target[key];
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    delete target[key];
  }
}

function cleanDatedEntries(
  target: JsonRecord,
  enabledKey: string,
  entriesKey: string,
  dateKey: string,
): void {
  if (target[enabledKey] !== true) {
    target[entriesKey] = [];
    return;
  }

  const entries = target[entriesKey];
  if (!Array.isArray(entries)) {
    return;
  }

  target[entriesKey] = entries.map((entry) => {
    if (!isRecord(entry)) {
      return entry;
    }

    removeBlankDate(entry, dateKey);
    return entry;
  });
}

function cleanDriverDates(driver: JsonRecord): void {
  removeBlankDate(driver, "dateOfBirth");
  removeBlankDate(driver, "testPassDate");

  if (driver.isUKResidentSinceBirth === true) {
    delete driver.dateMovedToUK;
  } else {
    removeBlankDate(driver, "dateMovedToUK");
  }

  cleanDatedEntries(
    driver,
    "motoringConvictions",
    "motoringConvictionEntries",
    "dateOfMotoringConviction",
  );
  cleanDatedEntries(
    driver,
    "unspentCriminalConvictionsNonMotoring",
    "nonMotoringConvictions",
    "dateOfNonMotoringConviction",
  );
  cleanDatedEntries(driver, "isMotorClaims", "motorClaims", "dateOfMotorClaim");
}

function buildDaveInfo(lead: Lead): JsonRecord {
  const clonedInfo = cloneJsonValue(lead.info ?? {});
  const info = isRecord(clonedInfo) ? clonedInfo : {};

  for (const field of dateFields) {
    removeBlankDate(info, field);
  }

  cleanDriverDates(info);

  if (info.isVehiclePurchased !== true) {
    delete info.purchaseDate;
  }

  if (info.isNoClaimsBonus !== true) {
    delete info.currentPolicyExpiryDate;
    delete info.overCurrentPolicyExpiryDate;
    delete info.underCurrentPolicyExpiryDate;
  }

  if (Array.isArray(info.additionalDrivers)) {
    info.additionalDrivers = info.additionalDrivers.map((driver) => {
      if (!isRecord(driver)) {
        return driver;
      }

      cleanDriverDates(driver);
      return driver;
    });
  }

  return info;
}

export function buildDaveLeadPayload(lead: Lead) {
  return {
    id: lead.id,
    first_name: lead.first_name ?? "",
    surname: lead.surname ?? "",
    mobile: lead.mobile ?? "",
    other_phone: lead.other_phone ?? "",
    email: lead.email ?? "",
    address1: lead.address1 ?? "",
    address2: lead.address2 ?? "",
    type: lead.type ?? "",
    gclid: lead.gclid ?? "",
    partial: Boolean(lead.partial),
    info: buildDaveInfo(lead),
  };
}

export function serializeDaveLeadPayload(lead: Lead): string {
  return JSON.stringify(buildDaveLeadPayload(lead), null, 2);
}
